import { useCallback, useRef, useState } from "react";

/**
 * SINGLE-FLIGHT-PRIMITIVE-1 (s170, D-169.10) — the synchronous interlock for duplicating actions.
 *
 * ## The defect this exists to prevent
 *
 * `disabled={mutation.isPending}` is NOT an interlock. It engages only after a React re-render, so
 * every click landing inside one tick reads the pre-render value and each fires a real mutation.
 * Proven in s169: three synchronous clicks at a correctly-wired `disabled` button produced THREE
 * requests. For a *duplicating* action — a send, a mint, a create, an append-only audit event —
 * that is three real-world effects.
 *
 * The lock below flips SYNCHRONOUSLY, before React is involved at all, so the second click of a
 * double-click is rejected outright. `disabled` stays as the visible affordance on top of it; this
 * is the actual interlock.
 *
 * ## Why the lock releases on the PROMISE, not `onSettled`
 *
 * Ported verbatim from the s169 TaskDrawer rationale, because this is the part that looks like a
 * free choice and is not:
 *
 * > Releasing from `onSettled` instead looked equivalent but was not: the mutation's `isPending`
 * > can clear before the callback runs, so the button re-enabled while the lock was still held and
 * > the next click was silently swallowed. Tying the lock to the promise makes the two windows
 * > provably identical.
 *
 * A swallowed legitimate click reads to the user exactly like a missed click — which is the failure
 * Reade's Test 2 caught in the s169 first cut. The two s169 hand-rolled copies had already drifted
 * apart on precisely this point (TaskDrawer on the promise, ExecutorTaskList on `onSettled`); this
 * hook is the one place that decision now lives.
 *
 * The window held is the MUTATION window, never the refetch window. Holding through a background
 * invalidation leaves the control dead for seconds — fire the refetch without awaiting it.
 *
 * ## Scope
 *
 * This wraps the ACTION, not the button. Several duplicating fires in this app have no button at
 * all — a dialog-open effect that mints an upload link, two file-drop handlers that start billable
 * extraction, a dialog-confirm callback that appends an audit event. A button component could not
 * guard those; a thunk wrapper can.
 *
 * ## Usage
 *
 * ```tsx
 * const send = useSingleFlight();
 *
 * // Awaitable — resolves with the value, REJECTS on error, resolves `undefined` when skipped.
 * const outcome = await send.run(() => sendSmsMutation.mutateAsync(vars));
 * if (!outcome) return;            // a skipped duplicate does nothing, by design
 *
 * // Fire-and-forget — errors surfaced by the mutation's own onError.
 * <button onClick={() => send.runQuiet(() => markDone.mutateAsync())}
 *         disabled={send.inFlight}>…</button>
 * ```
 *
 * One instance = one lock. Give mutually-exclusive actions a SHARED instance (the drawer's
 * done/undo/dismiss do this deliberately); give independent actions their own.
 */
export interface SingleFlight {
  /**
   * Runs `fire` unless a call is already in flight. Resolves with `fire`'s value, or `undefined`
   * when the call was skipped as a duplicate. REJECTS if `fire` rejects — callers that await this
   * keep their own try/catch. Use {@link runQuiet} for fire-and-forget.
   */
  run: <T>(fire: () => Promise<T>) => Promise<T | undefined>;
  /**
   * Fire-and-forget: same interlock, but the rejection is absorbed here rather than becoming an
   * unhandled rejection. Use only where the failure is already surfaced elsewhere (a mutation's
   * `onError`, a toast).
   */
  runQuiet: (fire: () => Promise<unknown>) => void;
  /** True while a call is in flight. Feed to `disabled` as the visible affordance. */
  inFlight: boolean;
  /**
   * The lock's SYNCHRONOUS value. `inFlight` is React state and lags by a render; this does not.
   * Only needed when something outside a click handler must ask "is one already running?" — an
   * effect, for instance. A click handler should just call `run`/`runQuiet` and let it decide.
   */
  isLocked: () => boolean;
}

export function useSingleFlight(): SingleFlight {
  const lock = useRef(false);
  const [inFlight, setInFlight] = useState(false);

  const run = useCallback(<T,>(fire: () => Promise<T>): Promise<T | undefined> => {
    // Synchronous rejection of the duplicate. This line is the interlock; everything below is
    // bookkeeping and affordance.
    if (lock.current) return Promise.resolve(undefined);
    lock.current = true;
    setInFlight(true);
    // Release is tied to the promise `fire` returns — see the header note. `fire()` is called
    // inside the try so a synchronous throw still releases the lock rather than wedging it.
    let promise: Promise<T>;
    try {
      promise = fire();
    } catch (e) {
      lock.current = false;
      setInFlight(false);
      return Promise.reject(e);
    }
    return promise.finally(() => {
      lock.current = false;
      setInFlight(false);
    });
  }, []);

  const runQuiet = useCallback(
    (fire: () => Promise<unknown>) => {
      void run(fire).catch(() => {
        /* surfaced by the caller's own onError / toast */
      });
    },
    [run],
  );

  const isLocked = useCallback(() => lock.current, []);

  return { run, runQuiet, inFlight, isLocked };
}
